import { PRIVATE_ROUTES } from "Constants";
import type { MenuItem } from "Components/SideBar/types";
import ReportsSvg from "Assets/icons/reports.svg";
import { TFunction } from "react-i18next";
import NetworkSvg from 'Assets/icons/new-network.svg';

export type Indexed = { node: MenuItem; parentKey: string | null };

export const normPath = (p?: string) => (p || "").toLowerCase().replace(/\/+$/, "");

export function getFirstSegmentAfterApp(pathname: string): string | undefined {
  const norm = normPath(pathname);
  const parts = norm.split("/").filter(Boolean);
  if (parts[0] !== "app") return undefined;
  return parts[1];
}

export function stripMnt(slug: string): string {
  return slug.replace(/-mnt$/i, "");
}

export function makeIndex(trees: MenuItem[]): Map<string, Indexed[]> {
  const map = new Map<string, Indexed[]>();

  const add = (key: string, entry: Indexed) => {
    const list = map.get(key) ?? [];
    list.push(entry);
    map.set(key, list);
  };

  const walk = (nodes: MenuItem[], parentKey: string | null) => {
    for (const n of nodes) {
      const k = (n.key || "").toLowerCase();
      if (!k) continue;
      add(k, { node: n, parentKey });
      if (n.children?.length) walk(n.children, k);
    }
  };

  walk(trees, null);
  return map;
}

export function flattenTree(
  nodes: MenuItem[],
  parentKey: string | null = null,
  acc: Indexed[] = []
) {
  for (const n of nodes) {
    const k = (n.key || "").toLowerCase();
    acc.push({ node: n, parentKey });
    if (n.children?.length) flattenTree(n.children, k || parentKey, acc);
  }
  return acc;
}

export function buildPathFromFlat(flat: Indexed[], start: Indexed): MenuItem[] {
  const path: MenuItem[] = [start.node];
  let current: Indexed | undefined = start;

  while (current?.parentKey) {
    const parent = flat.find(
      (f) =>
        (f.node.key || "").toLowerCase() === current!.parentKey &&
        (f.node.children ?? []).some((c) => c === current!.node)
    );
    if (!parent) break;
    path.unshift(parent.node);
    current = parent;
  }

  return path;
}

export function buildPath(index: Map<string, Indexed[]>, deepestKey: string): MenuItem[] {
  const candidates = index.get(deepestKey) ?? [];
  if (candidates.length === 0) return [];

  let current = candidates[0];
  const path: MenuItem[] = [current.node];

  let childKey: string | null = deepestKey;
  let parentKey: string | null = current.parentKey;

  while (parentKey) {
    const parents = index.get(parentKey) ?? [];
    const realParent =
      parents.find((p) =>
        (p.node.children ?? []).some((c) => (c.key || "").toLowerCase() === childKey)
      ) ?? parents[0];

    if (!realParent) break;

    path.unshift(realParent.node);
    childKey = parentKey;
    parentKey = realParent.parentKey;
  }

  return path;
}

export function extractSettingsState(location: { state?: any; search?: string }) {
  const fromState = (location.state ?? {}) as Record<string, string | undefined>;
  const params = new URLSearchParams(location.search || "");

  const fromQuery = {
    tabKey: params.get("tabKey") || undefined,
    subTabKey: params.get("subTabKey") || undefined,
    subSubTabKey: params.get("subSubTabKey") || undefined,
  };


  const response = {
    tabKey: (fromState.tabKey || fromQuery.tabKey)?.toString(),
    subTabKey: (fromState.subTabKey || fromQuery.subTabKey)?.toString(),
    subSubTabKey: (fromState.subSubTabKey || fromQuery.subSubTabKey)?.toString(),
  };

  return response;
}

export function nodeStateMatches(
  node: MenuItem,
  current: ReturnType<typeof extractSettingsState>
): number {
  const s = (node as any).state as
    | { tabKey?: string; subTabKey?: string; subSubTabKey?: string }
    | undefined;
  if (!s) return 0;

  let score = 0;

  if (s.tabKey && current.tabKey && s.tabKey.toLowerCase() === current.tabKey.toLowerCase()) {
    score += 10;
  } else if (s.tabKey) {
    return -1;
  }

  if (
    s.subTabKey &&
    current.subTabKey &&
    s.subTabKey.toLowerCase() === current.subTabKey.toLowerCase()
  ) {
    score += 5;
  } else if (s.subTabKey) {
    return -1;
  }

  if (
    s.subSubTabKey &&
    current.subSubTabKey &&
    s.subSubTabKey.toLowerCase() === current.subSubTabKey.toLowerCase()
  ) {
    score += 2;
  } else if (s.subSubTabKey) {
    return -1;
  }

  return score;
}

export function extractReportsFilter(location: {
  state?: any;
  search?: string;
}): string | undefined {
  const fromState = (location.state ?? {}) as Record<string, string | undefined>;
  const params = new URLSearchParams(location.search || "");
  return (fromState.reportsFilter ?? params.get("reportsFilter") ?? undefined)?.toString();
}

export function nodeReportsMatches(node: MenuItem, currentFilter?: string): number {
  const s = (node as any).state as { reportsFilter?: string; reportsName?: string } | undefined;
  if (!s || !s.reportsFilter) return currentFilter ? 0 : 0;
  if (!currentFilter) return -1;
  return s.reportsFilter.toLowerCase() === currentFilter.toLowerCase() ? 7 : -1;
}

export function getBreadcrumb(
  location: { pathname: string; search?: string; state?: any },
  trees: MenuItem[],
  currentUser?: any,
  t?: TFunction<"translation", undefined>
): MenuItem[] {
  const index = makeIndex(trees);
  const flat = flattenTree(trees);
  const currentPath = normPath(location.pathname);

  const isSettings = currentPath.startsWith(normPath(PRIVATE_ROUTES.SETTINGS));
  const isAnalytics = currentPath.startsWith(normPath(PRIVATE_ROUTES.ANALYTICS));
  const reportsFilter = isAnalytics ? extractReportsFilter(location) : undefined;

  let best: Indexed | undefined;
  let bestScore = -1;

  const settingsState = isSettings ? extractSettingsState(location) : undefined;

  for (const f of flat) {
    const p = normPath(f.node.path);

    if (!p) continue;
    if (!(currentPath.startsWith(p) || p.startsWith(currentPath))) continue;

    let score = p.length;

    if (isSettings && settingsState) {
      const s = nodeStateMatches(f.node, settingsState);
      if (s < 0) continue;
      score += s;
    }

    if (isAnalytics) {
      const r = nodeReportsMatches(f.node, reportsFilter);
      if (r < 0) continue;
      score += r;
    }

    if (score > bestScore) {
      best = f;
      bestScore = score;
    }
  }

  if ((!best || best.parentKey === "reports") && isAnalytics) {
    const isFromFavoriteReport = currentUser?.favoritesReports?.find(
      (report: any) => report.name?.toLowerCase() === location.state?.name?.toLowerCase()
    );
    const crumbs: MenuItem[] = [
      {
        icon: ReportsSvg,
        key: "reports",
        label: t?.("reports"),
        ui: "accordion",
        children: [],
      } as MenuItem,
    ];

    if (isFromFavoriteReport || best?.node?.key === "favorites") {
      crumbs.push({
        icon: ReportsSvg,
        key: "favorites",
        label: t?.("favorites")!,
        path: "/app/analytics",
        state: { ...(location?.state || {}), reportsFilter: "favorites" },
      });
    } else {
      crumbs.push({
        icon: ReportsSvg,
        key: location?.state?.reportGroup?.group?.toLowerCase() ?? location?.state?.reportsFilter,
        label:
          location?.state?.reportGroup?.group ??
          location?.state?.reportsFilter?.charAt(0).toUpperCase() +
            location?.state?.reportsFilter?.slice(1),
        path: "/app/analytics",
        state: {
          ...(location?.state || {}),
          reportsFilter:
            location?.state?.reportGroup?.group?.toLowerCase() ??
            location?.state?.reportsFilter?.toLowerCase(),
        },
      });
    }

    if (!!location.state?.reportGroup) {
      crumbs.push({
        icon: ReportsSvg,
        key: location?.state?.reportGroup.group?.toLowerCase(),
        label: location?.state?.name!,
        state: { ...(location.state || {}), reportsFilter: location?.state?.reportGroup?.group },
      });
    }

    return crumbs as MenuItem[];
  }

  if (!best?.parentKey && best?.node?.key === "network") {
    const crumbs: MenuItem[] = [
      {
        icon: NetworkSvg,
        key: "network",
        label: t?.("network"),
        path: "/app/network",
        ui: "accordion",
        children: [],
      } as MenuItem,
    ];

    if (!!location?.state?.tab?.trim()?.length) {
      crumbs.push({
        icon: NetworkSvg,
        key: location?.state?.tab?.toLowerCase(),
        label: t?.(location?.state?.tab)!,
        state: { ...(location.state || {}), tab: location?.state?.tab },
      });
    }

    return crumbs as MenuItem[];
  }

  if (best) {
    return buildPathFromFlat(flat, best);
  }

  const segRaw = getFirstSegmentAfterApp(currentPath);
  if (segRaw) {
    const seg = stripMnt(segRaw);
    return [
      {
        key: seg,
        label: seg,
        path: currentPath,
        icon: undefined,
      } as MenuItem,
    ];
  }

  const segments = currentPath
    .split("/")
    .filter(Boolean)
    .map((s) => s.toLowerCase())
    .filter((s) => index.has(s));

  if (segments.length > 0) {
    const deepest = segments[segments.length - 1];
    const viaKey = buildPath(index, deepest);
    if (viaKey.length) return viaKey;
  }

  const over = buildPath(index, "overview");
  if (over.length) return over;
  const dash = buildPath(index, "dashboard");
  if (dash.length) return dash;
  return [];
}

export function findNodeByKey(trees: MenuItem[], key: string): MenuItem | null {
  const stack: MenuItem[] = [...trees];
  const low = key.toLowerCase();
  while (stack.length) {
    const n = stack.pop()!;
    if ((n.key || "").toLowerCase() === low) return n;
    if (n.children?.length) stack.push(...n.children);
  }
  return null;
}

export function getManualCrumbs(pathname: string, trees: MenuItem[]): MenuItem[] | null {
  const p = normPath(pathname);

  if (p === normPath("/app/analytics/manage-email-subscriptions")) {
    const reportsNode =
      findNodeByKey(trees, "reports") ??
      ({
        key: "reports",
        label: "Reports",
        path: "/app/analytics",
      } as MenuItem);

    const manageEmailNode: MenuItem = {
      key: "manage-email-subscriptions",
      label: "Manage Email Subscriptions",
      path: "/app/analytics/manage-email-subscriptions",
    };

    return [reportsNode, manageEmailNode];
  }

  return null;
}

type Alias = { from: string; to: string };

const derivedAliases: Alias[] = Object.entries(PRIVATE_ROUTES)
  .filter(([key]) => key.endsWith("_MNT"))
  .map(([mntKey, mntPath]) => {
    const baseKey = mntKey.replace(/_MNT$/, "");
    const basePath = (PRIVATE_ROUTES as any)[baseKey];
    return basePath ? { from: String(mntPath), to: String(basePath) } : null;
  })
  .filter(Boolean) as Alias[];

const manualAliases: Alias[] = [
  { from: PRIVATE_ROUTES.NETWORK_CUSTOMER_MNT, to: PRIVATE_ROUTES.CUSTOMERS },
  { from: PRIVATE_ROUTES.NETWORK_AGENT_MNT, to: PRIVATE_ROUTES.NETWORK },
  { from: PRIVATE_ROUTES.NETWORK_ACCOUNT_MNT, to: PRIVATE_ROUTES.NETWORK },

  { from: PRIVATE_ROUTES.SHIPMENTS_TRACKING_MNT, to: PRIVATE_ROUTES.SHIPMENTS_TRACKING },

  { from: PRIVATE_ROUTES.TRANSSHIPMENTS_MNT, to: PRIVATE_ROUTES.TRANSSHIPMENTS },

  { from: PRIVATE_ROUTES.EQUIPMENT_MNT, to: PRIVATE_ROUTES.SETTINGS },
  { from: PRIVATE_ROUTES.SUBCONTRACTORS_MNT, to: PRIVATE_ROUTES.SETTINGS },
  { from: PRIVATE_ROUTES.MEMBERS_MNT, to: PRIVATE_ROUTES.SETTINGS },
  { from: PRIVATE_ROUTES.TEMPLATE_CREATE, to: PRIVATE_ROUTES.SETTINGS },
];

const aliases: Alias[] = [...manualAliases, ...derivedAliases];

export function normalizeBreadcrumbPath(pathname: string): string {
  const clean = pathname.replace(/\/+$/, "");

  for (const { from, to } of aliases) {
    if (clean === from || clean.startsWith(from + "/")) {
      return to;
    }
  }
  return clean;
}
